import React, { useContext, useMemo } from 'react';

import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';

import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import { ClassicEditorContext } from '@confluence/editor-features/entry-points/ClassicEditorContext.ts';
import { createHeadingLink } from '@confluence/editor-heading-links/entry-points/createHeadingLink.ts';
import { expValEquals } from '@confluence/feature-experiments';
import {
	createNestedRendererData,
	type NestedAdfRendererWrapper,
	type NestedAdfRendererWrapperProps,
} from '@confluence/macro-utils/entry-points/nestedAdfRenderer.ts';

type NestedAdfRendererWrapperComponentProps = NestedAdfRendererWrapperProps & {
	Renderer: React.FC<any>;
	appearance?: string;
	rendererExtensionOptions: React.ContextType<typeof RendererExtensionContext>;
	getHeadingLinkPath?: () => string | null;
};

export type NestedAdfRendererWrapperProviderProps = {
	children: (RendererWrapper: NestedAdfRendererWrapper) => React.ReactNode;
	Renderer: React.FC<any>;
	appearance?: string;
};

const NestedAdfRendererWrapperComponent = ({
	Renderer,
	appearance,
	rendererExtensionOptions,
	adf,
	targetContentId,
	targetSpaceKey,
	mediaToken,
	headingIdPrefix,
	getHeadingLink,
	getHeadingLinkPath,
}: NestedAdfRendererWrapperComponentProps) => {
	const rendererAppearance = appearance === 'full-page' ? 'full-page' : 'full-width';
	const rendererData = useMemo(() => createNestedRendererData(adf), [adf]);
	const allowHeadingAnchorLinks = useMemo(
		() =>
			expValEquals('confluence_toc_included_content_exp', 'isEnabled', true)
				? {
						allowNestedHeaderLinks: true,
						getHeadingLink:
							getHeadingLink ??
							((headingId: string) =>
								// SSR-safe: fall back to a relative in-page anchor rather than
								// reading `window.location.href`, which throws during SSR.
								createHeadingLink(getHeadingLinkPath, headingId) ??
								`#${encodeURIComponent(headingId)}`),
					}
				: undefined,
		[getHeadingLink, getHeadingLinkPath],
	);

	return (
		<RendererActionsContext>
			<RendererExtensionContext.Provider value={rendererExtensionOptions}>
				<Renderer
					contentId={targetContentId}
					spaceKey={targetSpaceKey}
					data={rendererData}
					appearance={rendererAppearance}
					hasInlineComments={false}
					mediaToken={mediaToken}
					headingIdPrefix={headingIdPrefix}
					allowHeadingAnchorLinks={allowHeadingAnchorLinks}
				/>
			</RendererExtensionContext.Provider>
		</RendererActionsContext>
	);
};

export const NestedAdfRendererWrapperProvider = ({
	children,
	Renderer,
	appearance,
}: NestedAdfRendererWrapperProviderProps): React.JSX.Element => {
	const { getExtensionHandlers, populateExtensionProviders, getHeadingLinkPath } =
		useContext(ClassicEditorContext);

	const contextRendererExtensionOptions = useMemo(
		() => ({
			getExtensionHandlers,
			populateExtensionProviders,
		}),
		[getExtensionHandlers, populateExtensionProviders],
	);

	const RendererWrapper = useMemo(
		() => (props: NestedAdfRendererWrapperProps) => (
			<NestedAdfRendererWrapperComponent
				{...props}
				Renderer={Renderer}
				appearance={appearance}
				getHeadingLinkPath={getHeadingLinkPath}
				rendererExtensionOptions={contextRendererExtensionOptions}
			/>
		),
		[appearance, Renderer, contextRendererExtensionOptions, getHeadingLinkPath],
	);

	return <>{children(RendererWrapper)}</>;
};
